// CreateRoom - Facilitator interface for creating new retrospective rooms
// Implements US-1.4: Create Room UI

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { TEMPLATES } from '../config/templates';
import { createRoom, joinRoom } from '../services/api';
import { getIconComponent } from '../utils/iconMapping';
import insurtaleLogo from '../assets/insurtale-logo.webp';
import styles from './CreateRoom.module.css';

type VotingMode = 'dot_voting' | 'none';

function CreateRoom() {
  const navigate = useNavigate();

  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [roomName, setRoomName] = useState<string>('');
  const [anonymousMode, setAnonymousMode] = useState<boolean>(true);
  const [votingMode, setVotingMode] = useState<VotingMode>('dot_voting');

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // HANDLE form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTemplate) {
      setError('Bitte wähle ein Template');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // CREATE room via API
      const createResponse = await createRoom({
        template: selectedTemplate,
        name: roomName || undefined,
        anonymousMode,
        votingMode,
      });

      // FACILITATOR auto-joins their own room to get session token
      // This is required because RoomBoard needs a session ID to function
      const joinResponse = await joinRoom(createResponse.room.code);

      // STORE session token (facilitator's session in their own room)
      localStorage.setItem('elegy_session_id', joinResponse.session_token);

      // REDIRECT to room board with room code for easy sharing
      navigate(`/room/${createResponse.room.id}`, {
        state: {
          isNewRoom: true,
          roomCode: createResponse.room.code,
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Raum konnte nicht erstellt werden');
      setLoading(false);
    }
  };

  // GET template cards array for rendering
  const templateCards = Object.values(TEMPLATES);

  return (
    <div className={styles.createRoomContainer}>
      <div className={styles.createRoomCard}>
        <div className={styles.createRoomHeader}>
          <img src={insurtaleLogo} alt="insur_tale" className={styles.logo} />
          <h2>Retro erstellen</h2>
          <p>Richte deine Retro in unter 60 Sekunden ein</p>
        </div>

        <form className={styles.createRoomForm} onSubmit={handleSubmit}>
          {/* Template Selection */}
          <div className={styles.formSection}>
            <label className={styles.sectionLabel}>
              Wähle ein Template <span className={styles.required}>*</span>
            </label>
            <div className={styles.templateGrid}>
              {templateCards.map((template) => (
                <div
                  key={template.id}
                  className={`${styles.templateCard} ${selectedTemplate === template.id ? styles.selected : ''}`}
                  onClick={() => setSelectedTemplate(template.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedTemplate(template.id);
                    }
                  }}
                >
                  <div className={styles.templateHeader}>
                    <span className={styles.templateIcon}>{getIconComponent(template.templateIcon || template.columns[0]?.icon || '📋', 28)}</span>
                    <h3>{template.name}</h3>
                  </div>
                  <div className={styles.templateColumns}>
                    {template.columns
                      .filter(col => col.key !== 'actions')
                      .map((col) => (
                        <div key={col.key} className={styles.templateColumnPreview}>
                          <span className={styles.columnIcon}>{getIconComponent(col.icon, 16)}</span>
                          <span className={styles.columnLabel}>{col.label}</span>
                        </div>
                      ))}
                  </div>
                  {selectedTemplate === template.id && (
                    <div className={styles.templateSelectedIndicator}>✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Room Name (Optional) */}
          <div className={styles.formSection}>
            <label className={styles.sectionLabel} htmlFor="roomName">
              Raum-Name <span className={styles.optional}>(optional)</span>
            </label>
            <input
              id="roomName"
              type="text"
              className={styles.roomNameInput}
              placeholder="z.B. Sprint 24 Retrospektive"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Settings */}
          <div className={styles.formSection}>
            <label className={styles.sectionLabel}>Einstellungen</label>

            <div className={styles.settingsGroup}>
              {/* Anonymous Mode Toggle */}
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <span className={styles.settingLabel}>Anonymer Modus</span>
                  <span className={styles.settingDescription}>
                    Teilnehmernamen auf Karten verbergen
                  </span>
                </div>
                <button
                  type="button"
                  className={`${styles.toggle} ${anonymousMode ? styles.active : ''}`}
                  onClick={() => setAnonymousMode(!anonymousMode)}
                  aria-label="Toggle anonymous mode"
                >
                  <span className={styles.toggleSlider} />
                </button>
              </div>

              {/* Voting Mode Select */}
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <span className={styles.settingLabel}>Abstimmungs-Modus</span>
                  <span className={styles.settingDescription}>
                    Wie das Team Themen priorisiert
                  </span>
                </div>
                <select
                  className={styles.votingSelect}
                  value={votingMode}
                  onChange={(e) => setVotingMode(e.target.value as VotingMode)}
                >
                  <option value="dot_voting">Dot Voting</option>
                  <option value="none">Keine Abstimmung</option>
                </select>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className={styles.errorMessage}>
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className={styles.createButton}
            disabled={!selectedTemplate || loading}
          >
            {loading ? 'Raum wird erstellt...' : 'Raum erstellen'}
          </button>
        </form>

        <div className={styles.createRoomFooter}>
          <p>
            An bestehender Retro teilnehmen?{' '}
            <a href="/join">Raum-Code eingeben</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default CreateRoom;
